<script setup>
import { useAlert } from 'dashboard/composables';
import AddCanned from './AddCanned.vue';
import EditCanned from './EditCanned.vue';
import CannedResponsesDataTable from './CannedResponsesDataTable.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { picoSearch } from '@scmmishra/pico-search';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const showAddPopup = ref(false);
const loading = ref({});
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const activeResponse = ref({});
const searchQuery = ref('');
const topicFilter = ref('all');

const records = computed(() => getters.getSortedCannedResponses.value('asc'));

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, [
    { name: 'short_code', weight: 4 },
    'content',
  ]);
});

const uiFlags = computed(() => getters.getUIFlags.value);

const filterOptions = computed(() => [
  { value: 'all', label: t('CANNED_MGMT.FILTER_ALL') },
]);

const deleteConfirmText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.YES')} ${activeResponse.value.short_code}`
);

const deleteRejectText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.NO')} ${activeResponse.value.short_code}`
);

const deleteMessage = computed(() => {
  return ` ${activeResponse.value.short_code} ? `;
});

const fetchCannedResponses = async () => {
  try {
    await store.dispatch('getCannedResponse');
  } catch {
    // Ignore Error
  }
};

onMounted(() => {
  fetchCannedResponses();
});

const showAlertMessage = message => {
  loading.value[activeResponse.value.id] = false;
  activeResponse.value = {};
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  activeResponse.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  activeResponse.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteCannedResponse = async id => {
  try {
    await store.dispatch('deleteCannedResponse', id);
    showAlertMessage(t('CANNED_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CANNED_MGMT.DELETE.API.ERROR_MESSAGE');
    showAlertMessage(errorMessage);
  }
};

const confirmDeletion = () => {
  loading.value[activeResponse.value.id] = true;
  closeDeletePopup();
  deleteCannedResponse(activeResponse.value.id);
};

function exportCannedList() {
  const payload = records.value.map(r => ({
    id: r.id,
    short_code: r.short_code,
    content: r.content,
    created_at: r.created_at,
    media_attachments: r.media_attachments || [],
  }));
  const data = JSON.stringify(payload, null, 2);
  const blob = new Blob([data], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `canned-responses-export.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="$t('CANNED_MGMT.LOADING')"
    :no-records-found="false"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('CANNED_MGMT.HEADER')"
        :description="$t('CANNED_MGMT.DESCRIPTION')"
        :link-text="$t('CANNED_MGMT.LEARN_MORE')"
        :search-placeholder="$t('CANNED_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="canned_responses"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('CANNED_MGMT.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            icon="i-lucide-plus"
            :label="$t('CANNED_MGMT.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
        <template #filters>
          <div class="flex flex-wrap items-center gap-2">
            <div class="min-w-[11rem]">
              <Select v-model="topicFilter" :options="filterOptions" />
            </div>
            <Button
              v-tooltip.top="$t('CANNED_MGMT.EXPORT_TITLE')"
              slate
              faded
              sm
              icon="i-lucide-download"
              :aria-label="$t('CANNED_MGMT.EXPORT_TITLE')"
              @click="exportCannedList"
            />
          </div>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div
        v-if="!records.length && !searchQuery.trim()"
        class="flex flex-col items-center justify-center px-0 py-16 text-center"
      >
        <div
          class="mb-6 flex size-24 items-center justify-center rounded-full bg-n-alpha-2 outline outline-1 outline-n-weak -outline-offset-1"
        >
          <Icon
            icon="i-lucide-message-square-quote"
            class="size-12 text-n-slate-11"
          />
        </div>
        <h3 class="mb-2 text-lg font-semibold text-n-slate-12">
          {{ $t('CANNED_MGMT.EMPTY_STATE.TITLE') }}
        </h3>
        <p class="mb-8 max-w-md text-body-main text-n-slate-11">
          {{ $t('CANNED_MGMT.EMPTY_STATE.DESCRIPTION') }}
        </p>
        <Button
          icon="i-lucide-plus"
          :label="$t('CANNED_MGMT.EMPTY_STATE.BUTTON')"
          @click="openAddPopup"
        />
      </div>

      <span
        v-else-if="!filteredRecords.length && searchQuery.trim()"
        class="flex flex-1 items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('CANNED_MGMT.NO_RESULTS') }}
      </span>

      <div v-else class="flex flex-col gap-3 pt-4">
        <CannedResponsesDataTable
          :items="filteredRecords"
          :loading="loading"
          @edit="openEditPopup"
          @delete="openDeletePopup"
        />
      </div>
    </template>
    <woot-modal
      v-model:show="showAddPopup"
      size="compact"
      :on-close="hideAddPopup"
    >
      <AddCanned :on-close="hideAddPopup" />
    </woot-modal>

    <woot-modal
      v-model:show="showEditPopup"
      size="compact"
      :on-close="hideEditPopup"
    >
      <EditCanned
        v-if="showEditPopup"
        :id="activeResponse.id"
        :edshort-code="activeResponse.short_code"
        :edcontent="activeResponse.content"
        :media-attachment="activeResponse.media_attachment"
        :media-attachments="activeResponse.media_attachments"
        :on-close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CANNED_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('CANNED_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </SettingsLayout>
</template>
